import React, { useState, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { useUser, useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from './supabaseClient';

// Import components
import Auth from './components/Auth';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerificationPending from './components/VerificationPending';
import VerificationSuccess from './components/VerificationSuccess';
import Sidebar from './components/Sidebar';
import Messages from './components/Messages';
import Groups from './components/Groups';
import MemberList from './components/MemberList';
import MemberAuditTrail from './components/MemberAuditTrail';
import MessageHistory from './components/MessageHistory';
import EmailAuditLog from './components/EmailAuditLog';
import EmailTest from './components/EmailTest';
import EmailTemplateManager from './components/EmailTemplateManager';
import EmailSESTest from './components/EmailSESTest';
import Preferences from './components/Preferences';
import Profile from './components/Profile';
import ProfileSetup from './components/ProfileSetup';
import ConsentManagement from './components/ConsentManagement';
import ConsentHistory from './components/ConsentHistory';
import ViewConsentForm from './components/ViewConsentForm';
import HelpDialog from './components/HelpDialog';
import ConsentForm from './components/ConsentForm';
import PublicConsentForm from './components/PublicConsentForm';
import ManageMessageTemplates from './components/ManageMessageTemplates';
import MessageTemplateAuditLog from './components/MessageTemplateAuditLog';
import MenuManagement from './components/admin/MenuManagement';
import RoleManagement from './components/admin/RoleManagement';
import RolePermissions from './components/admin/RolePermissions';
import RoleRequests from './components/admin/RoleRequests';
import UserManagement from './components/admin/UserManagement';
import ReleaseLog from './components/admin/ReleaseLog';
import TestCaseManagement from './components/admin/TestCaseManagement';
import BulkMemberImport from './components/admin/BulkMemberImport';
import ImportHistoryLog from './components/admin/ImportHistoryLog';
import TransferMemberImport from './components/admin/TransferMemberImport';
import AuthenticatedLayout from './components/AuthenticatedLayout';

function App() {
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading: isAuthLoading } = useSessionContext();
  const [hasProfile, setHasProfile] = useState(true); 
  const [showHelp, setShowHelp] = useState(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false); 

  useEffect(() => {
    console.log('User state changed:', { user, isAuthLoading });
  }, [user]);

  useEffect(() => {
    console.log('Current location:', location.pathname);
  }, [location]);

  // Don't show anything while checking auth
  if (isAuthLoading) {
    return null;
  }

  const PrivateRoute = () => {
    if (!user) {
      console.log('No user, redirecting to login');
      return <Navigate to="/login" replace />;
    }

    return <Outlet />;
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Auth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verification-pending" element={<VerificationPending />} />
        <Route path="/verify" element={<VerificationSuccess />} />
        <Route path="/public-consent" element={<PublicConsentForm />} />
        <Route path="/public-releases" element={<ReleaseLog isAdminView={false} />} />
        
        {/* Protected Routes */}
        <Route element={<AuthenticatedLayout />}>
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<Navigate to="/send-messages" replace />} />
            <Route path="/send-messages" element={<Messages />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/members" element={<MemberList />} />
            <Route path="/member-audit" element={<MemberAuditTrail />} />
            <Route path="/message-history" element={<MessageHistory />} />
            <Route path="/manage-message-templates" element={<ManageMessageTemplates />} />
            <Route path="/message-template-audit" element={<MessageTemplateAuditLog />} />
            <Route path="/email-audit" element={<EmailAuditLog />} />
            <Route path="/email-test" element={<EmailTest />} />
            <Route path="/email-templates" element={<EmailTemplateManager />} />
            <Route path="/email-template-manager" element={<EmailTemplateManager />} />
            <Route path="/email-ses-test" element={<EmailSESTest />} />
            <Route path="/preferences" element={<Preferences />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile-setup" element={<ProfileSetup />} />
            <Route path="/consent-management" element={<ConsentManagement />} />
            <Route path="/consent-history" element={<ConsentHistory />} />
            <Route path="/view-consent/:id" element={<ViewConsentForm />} />
            <Route path="/admin/role-requests" element={<RoleRequests />} />
            <Route path="/admin/user-management" element={<UserManagement />} />
            <Route path="/admin/menu-management" element={<MenuManagement />} />
            <Route path="/admin/role-management" element={<RoleManagement />} />
            <Route path="/admin/role-permissions" element={<RolePermissions />} />
            <Route path="/admin/test-case-management" element={<TestCaseManagement />} />
            <Route path="/admin/releases" element={<ReleaseLog isAdminView={true} />} />
            <Route path="/admin/bulk-member-import" element={<BulkMemberImport />} />
            <Route path="/admin/import-history" element={<ImportHistoryLog />} />
            <Route path="/admin/transfer-import" element={<TransferMemberImport />} />
          </Route>
        </Route>
      </Routes>
      <HelpDialog isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </Box>
  );
}

export default App;
